import React, { useEffect, useRef } from 'react';
import {
  makeStyles,
  EVGrid,
  EVButton,
  EVAlert,
  EVTypography,
} from '@eagleview/ev-comp-library';
import PropTypes from 'prop-types';
import { RegistrationFieldErrorStates, RegistrationFieldNames } from '../../constants/AppConstants';

const useStyles = makeStyles(() => ({
  errorAlert: {
    background: '#FFCDD1 !important',
    width: '100%',
    borderBottom: 'solid 1px #BC1616',
    borderRadius: '0px',
  },
  errorAlertMessage: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  errorIcon: {
    color: '#BC1616 !important',
  },
  errorAlertHeading: {
    fontFamily: 'Noto Sans',
    fontWeight: '600',
    fontSize: '14px',
    lineHeight: '19px',
    color: '#1B202C',
  },
  errorsList: {
    '&>li>button': {
      display: 'inline-block',
      fontFamily: 'Noto Sans',
      fontWeight: '400',
      fontSize: '14px',
      lineHeight: '21px',
      textTransform: 'none',
      padding: 0,
      color: '#0A2E8F',
      '&>b': { fontWeight: '600' },
      '&>span': { textDecoration: 'underline' },
      '&:hover': { background: 'unset' },
    },
    '&>li::marker': {
      color: '#0A2E8F',
    },
    paddingLeft: '8px',
  },
  errorAlertDes: {
    fontFamily: 'Noto Sans',
    fontWeight: '400',
    fontSize: '14px',
    lineHeight: '18px',
    color: '#1B202C',
  },
}));

function ErrorsAlert({ inputErrors = {}, inputRefs }) {
  const classes = useStyles();
  const firstErrorRef = useRef();

  useEffect(() => {
    if (firstErrorRef.current) {
      firstErrorRef.current.focus();
    }
  }, [firstErrorRef]);

  const renderError = (field, error) => {
    if (!error || typeof error !== 'string' || error === RegistrationFieldErrorStates.VALID) return null;
    const fieldName = RegistrationFieldNames[field];
    if (field === 'appname' && error.includes('is already in use')) {
      return (
        <>
          App name must be unique: error
        </>
      );
    }
    if (error === RegistrationFieldErrorStates.INVALID) {
      return (
        <>
          {fieldName}
          is not valid
        </>
      );
    }
    if (error === RegistrationFieldErrorStates.MISSING) {
      return (
        <>
          {fieldName}
          is required
        </>
      );
    }
    if (error.startsWith(fieldName)) {
      const parts = error.split(fieldName);
      return (
        <>
          {fieldName}
          {parts[parts.length - 1]}
        </>
      );
    }
    if (field.startsWith('billing')) {
      return (
        <>
          {'Billing '}
          {error}
        </>
      );
    }
    return error;
  };

  const handleClick = (field) => {
    const ref = inputRefs[field]?.current;
    if (
      field === 'industry'
      || field === 'businessSegment'
      || field === 'country'
      || field === 'billingState'
      || field === 'contactState'
      || field === 'refer'
    ) {
      if (ref?.firstChild) {
        ref.firstChild.focus();
      }
    } else if (ref) {
      ref.focus();
    }
  };

  return (
    <EVAlert severity="error" className={classes.errorAlert} classes={{ icon: classes.errorIcon, message: classes.errorAlertMessage }}>
      <EVGrid container style={{ flexDirection: 'column', gap: '8px' }}>
        <EVTypography className={classes.errorAlertHeading}>
          Please complete all details
        </EVTypography>
        <EVTypography className={classes.errorAlertDes}>
          Check the following errors and submit again:
        </EVTypography>
      </EVGrid>
      <EVGrid className={classes.errorsList}>
        {Object.entries(inputErrors).map(([fieldName, error]) => {
          const errorString = renderError(fieldName, error);
          return (
            errorString && (
            <li key={fieldName}>
              <EVButton
                id={fieldName}
                ref={(el) => { if (!firstErrorRef.current) firstErrorRef.current = el; }}
                onClick={() => handleClick(fieldName)}
              >
                {errorString}
              </EVButton>
            </li>
            )
          );
        })}
      </EVGrid>
    </EVAlert>
  );
}

ErrorsAlert.propTypes = {
  inputErrors: PropTypes.shape().isRequired,
  inputRefs: PropTypes.shape().isRequired,
};

export default ErrorsAlert;
